import React, { useState, useEffect } from 'react';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Pagination from 'rc-pagination';
import _en_US from 'rc-pagination/lib/locale/en_US';

import AppActions from '../../actions/app-actions';
import { preformatWithRequestID } from '../../helpers';

import Loader from '../common/loader';
import PreferenceList from './preference-list';
import PreferenceDialog from './preference-dialog';

const pageLength = 200;

const Preference = () => {
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [list, setList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dlg, setDlg] = useState({ open: false });

  const showErrorMsg = (err, msg = '') => {
    const { body = {} } = err.res || {};
    const errormsg = body.error ? body.error.charAt(0).toUpperCase() + body.error.slice(1) 
      : 'Please check our connection';
    AppActions.setSnackbar(preformatWithRequestID(err.res, `${msg}. ${errormsg}`));
  };

  const handleCreatePreference = () => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (data) => {
      AppActions.createPreference(data)
        .then((cRes) => {
          AppActions.getPreferences()
            .then(res => {
              const list = Array.isArray(res.body) ? res.body : [];
              setCount(list.length);
              setList(list);
              if (Array.isArray(cRes.body)) {
                AppActions.setSnackbar(`These '${cRes.body.join(', ')}' attributes can't be override. Please check global preference`);
              }
            })
            .catch(err => {
              showErrorMsg(err, `Preferences couldn't be loaded`);
            })
            .finally(() => {
              setDlg(() => ({ open: false }));
            });
        })
        .catch((err) => {
          showErrorMsg(err);
        });
    };
    setDlg(() => ({ open: true, dlgType: 'create', handleCancel, handleOk }));
  };

  const handleEditPreference = (idx) => {
    const pref = list[idx] || {};
    const cAttr = Array.isArray(pref.attributes) ? pref.attributes.map(e => ({ ...e })) : [];
    const updatePref = { ...pref };
    let updateList = [];
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (mAttr) => {
      const uAttr = [];
      const dAttr = [];
      const sAttr = [];
      cAttr.forEach(attr => {
        const fAttr = mAttr.find(a => a.name === attr.name);
        if (fAttr) {
          // eslint-disable-next-line no-unused-vars
          const { isNew, ...rest} = fAttr;
          uAttr.push(rest);
          if ((attr.value !== fAttr.value) || (attr.description !== fAttr.description) || (attr.override !== fAttr.override)) {
            sAttr.push(rest);
          }
        } else {
          dAttr.push(attr);
        }
      });
      mAttr.forEach(a => {
        if (a.isNew) {
          // eslint-disable-next-line no-unused-vars
          const { isNew, ...rest} = a;
          uAttr.push(rest);
          sAttr.push(rest);
        }
      });
      updatePref.attributes = uAttr;
      updateList = list.map((l, i) => {
        if (i === idx) {
          return updatePref;
        }
        return l;
      });
      if (dAttr.length) {
        const names = dAttr.map(e => e.name);
        AppActions.deletePrefernceAttr(pref.id, names.join(','))
          .then(() => {
            if (sAttr.length) {
              AppActions.updatePrefernceAttr(pref.id, sAttr)
                .then(res => {
                  setCount(updateList.length);
                  setList(updateList);
                  if (Array.isArray(res)) {
                    AppActions.setSnackbar(`These '${res.join(', ')}' attributes can't be override. Please check global preference`);
                  }
                })
                .catch(err => {
                  showErrorMsg(err, `Editing preference failed`);
                })
            } else {
              setCount(updateList.length);
              setList(updateList);            
            }
          })
          .catch(err => {
            showErrorMsg(err, `Editing preference failed`);
          })
          .finally(() => {
            setDlg(() => ({ open: false }));
          });
      } else if (sAttr.length) {
        AppActions.updatePrefernceAttr(pref.id, sAttr)
          .then(res => {
            setCount(updateList.length);
            setList(updateList);
            if (Array.isArray(res)) {
              AppActions.setSnackbar(`These '${res.join(', ')}' attributes can't be override. Please check global preference`);
            }
          })
          .catch(err => {
            showErrorMsg(err, `Editing preference failed`);
          })
          .finally(() => {
            setDlg(() => ({ open: false }));
          });
      } else {
        setDlg(() => ({ open: false }));
        AppActions.setSnackbar('Nothing changed');
      }
    };
    setDlg(() => ({ open: true, dlgType: 'edit', handleOk, handleCancel, ...pref }));
  };

  const handleRemovePreference = (type, instance, id) => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      AppActions.deletePreference(id)
        .then(() => {
          AppActions.getPreferences()
            .then(res => {
              const list = Array.isArray(res.body) ? res.body : [];
              setCount(list.length);
              setList(list);
              setLoading(false);
            })
            .catch(err => {
              const errormsg = err.body && err.body.error || 'Please check our connection';
              AppActions.setSnackbar(preformatWithRequestID(err.res, 'Preferences couldn\'t be loaded. ' + errormsg));
            })
            .finally(() => {
              setDlg(() => ({ open: false }));
            });
        })
        .catch(err => {
          const errormsg = err.body && err.body.error || 'Please check our connection';
          AppActions.setSnackbar(preformatWithRequestID(err.res, 'Tenants couldn\'t be loaded. ' + errormsg));
        });
    };
    setDlg(() => ({ open: true, dlgType: 'remove', handleOk, handleCancel, type, instance }));    
  };

  useEffect(() => {
    AppActions.getPreferences()
      .then(res => {
        const list = Array.isArray(res.body) ? res.body : [];
        setCount(list.length);
        setList(list);
      })
      .catch(err => {
        const errormsg = err.body && err.body.error || 'Please check our connection';
        AppActions.setSnackbar(preformatWithRequestID(err.res, 'Preferences couldn\'t be loaded. ' + errormsg));
      })
      .finally(() => {
        setLoading(false);
      })
    return () => {};
  }, []);

  const handlePageChange = (pageNo) => {
    setPageLoading(true);
    AppActions.getTenants(pageNo, pageLength)
      .then(res => {
        setCurrentPage(pageNo);
        setList(res.body || []);
      })
      .catch(err => {
        const errormsg = err.body && err.body.error || 'Please check our connection';
        AppActions.setSnackbar(preformatWithRequestID(err.res, 'Tenants couldn\'t be loaded. ' + errormsg));
      })
      .finally(() => {
        setPageLoading(false);
      })
  };

  const loader = () => {
    return (
      <div>
        <Loader show={true} />
      </div>        
    );    
  };

  const bodyContent = () => {
    if (loading) {
      return loader();
    } else {
      return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container justify='flex-end'>
              <Grid item>
                <Button variant='contained' color='primary' onClick={handleCreatePreference}
                >Create New Preference</Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {
              pageLoading ? loader() :
                (<PreferenceList list={list} handleEditPreference={handleEditPreference} 
                  handleRemovePreference={handleRemovePreference}/>)
            }
          </Grid>
        </Grid>
        {
          list.length > 0 ? (
            <Pagination
              className="margin-top"
              locale={_en_US}
              simple
              pageSize={pageLength}
              current={currentPage}
              total={count}
              hideOnSinglePage
              onChange={e => handlePageChange(e)}
            /> 
          ) : null
        }
  
        {dlg.open ? <PreferenceDialog {...dlg} /> : null}
      </>
      );
    }
  };

  return (
    <>
      {bodyContent()}
    </>
  );
};

export default Preference;
